import { initSystemTheme } from "./lib/system-theme";
initSystemTheme();
import React from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "./i18n";
import App from "./App.tsx";
import "./index.css";

// Auto-reload on chunk load failure (stale cache after deploy)
const CHUNK_RELOAD_KEY = "chunk-reload-ts";
const CHUNK_RELOAD_WINDOW_MS = 10_000;

function handleChunkLoadFailure(message: string | undefined) {
  if (!message) return;
  const isChunkError =
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("error loading dynamically imported module") ||
    /Loading chunk \d+ failed/i.test(message);

  if (!isChunkError) return;

  const lastReloadTs = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
  const now = Date.now();
  if (now - lastReloadTs < CHUNK_RELOAD_WINDOW_MS) return;

  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  window.location.reload();
}

// Vite fires this when a lazy page file can't be fetched (e.g. after an update
// replaced the old file). React's error boundary would otherwise swallow it.
window.addEventListener("vite:preloadError", (e) => {
  const err = (e as Event & { payload?: Error }).payload;
  e.preventDefault();
  handleChunkLoadFailure(err?.message || "Failed to fetch dynamically imported module");
});

window.addEventListener("error", (e) => {
  handleChunkLoadFailure(e.message);
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = e.reason?.message || String(e.reason);
  handleChunkLoadFailure(msg);
});

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);
